<script setup lang="ts">
import { onMounted, computed, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import { PaperAirplaneIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { useVariableStore, type Variable } from '@/stores/useVariableStore'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useToast } from 'vue-toast-notification'

const $toast = useToast()
const variableStore = useVariableStore()
const usersStore = useBoostCoreUsersStore()
const boostCoreStore = useBoostCorePermissionStore()

const STATUS_OPTIONS = [
  { id: 'VALIDATION_MANAGER', value: 'VALIDATION_MANAGER', label: 'À envoyer (VALIDATION_MANAGER)' },
  { id: 'SENT_TO_LUCCA', value: 'SENT_TO_LUCCA', label: 'Envoyé (SENT_TO_LUCCA)' },
  { id: 'ALL', value: 'ALL', label: 'Tous' },
]
const BU_OPTIONS = [
  { id: 'ALL', value: null, label: 'Toutes les BU' },
  { id: 'UNIXO_ONE', value: 'UNIXO_ONE', label: 'UNIXO ONE' },
  { id: 'UNIXO_CORP', value: 'UNIXO_CORP', label: 'UNIXO CORP' },
  { id: 'UNIXO_FACTORY', value: 'UNIXO_FACTORY', label: 'UNIXO FACTORY' },
  { id: 'UNIXO_SECURE', value: 'UNIXO_SECURE', label: 'UNIXO SECURE' },
  { id: 'UNIXO_NEGOCE', value: 'UNIXO_NEGOCE', label: 'UNIXO NEGOCE' },
  { id: 'UNIXO_ACR', value: 'UNIXO_ACR', label: 'UNIXO ACR' },
  { id: 'UNIXO_CUSTOMER_SUCCESS', value: 'UNIXO_CUSTOMER_SUCCESS', label: 'UNIXO CUSTOMER SUCCESS' },
  { id: 'ODY_C', value: 'ODY_C', label: 'ODY C' },
  { id: 'OOB', value: 'OOB', label: 'OOB' },
  { id: 'GROUP_SOLUTIONS', value: 'GROUP_SOLUTIONS', label: 'GROUP SOLUTIONS' },
]

const rows = ref<Variable[]>([])
const loading = ref(false)
const sending = ref(false)
const deleting = ref(false)
const selectedRows = ref<Variable[]>([])
const lastResult = ref<{ sent: { uuid: string; luccaVariableId: number | null }[]; failed: { uuid: string; error: string }[] } | null>(null)
const showConfirmSend = ref(false)
const showConfirmDelete = ref(false)
const rowToDelete = ref<any>(null)
const DELETE_WINDOW_MS = 48 * 60 * 60 * 1000

const filterStatus = ref<string>('VALIDATION_MANAGER')
const filterBu = ref<string | null>(null)
const filterUserUuid = ref<string | null>(null)

const userOptions = computed(() => {
  const active = usersStore.users.filter((u) => u.status === 'ACTIVE')
  const opts = active.map((u) => ({ id: u.uuid, value: u.uuid, label: usersStore.getUserLabel(u) }))
  opts.unshift({ id: 'ALL', value: null as any, label: 'Tous les users' })
  return opts
})

function formatDateTime(d: string | Date | null | undefined): string {
  if (!d) return '-'
  const date = typeof d === 'string' ? new Date(d) : d
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
function formatAmount(v: number | null | undefined): string {
  if (v == null) return '-'
  return `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
}
function userLabel(u: any): string {
  if (!u) return '-'
  return [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || '-'
}

async function loadVariables() {
  loading.value = true
  try {
    const statuses = filterStatus.value === 'ALL' ? ['VALIDATION_MANAGER', 'SENT_TO_LUCCA'] : [filterStatus.value]
    const payload: { statuses?: string[]; userUuids?: string[]; bus?: string[] } = { statuses }
    if (filterUserUuid.value) payload.userUuids = [filterUserUuid.value]
    if (filterBu.value) payload.bus = [filterBu.value]
    rows.value = await variableStore.fetchLuccaAdmin(payload)
    selectedRows.value = []
  } finally {
    loading.value = false
  }
}

const gridRows = computed(() =>
  rows.value.map((v: any) => {
    const sentAt = v.sentToLuccaAt ? new Date(v.sentToLuccaAt).getTime() : null
    const canDelete =
      v.variableStatus === 'SENT_TO_LUCCA' &&
      v.luccaVariableId != null &&
      sentAt != null &&
      Date.now() - sentAt < DELETE_WINDOW_MS
    return {
      uuid: v.uuid,
      salesEngineer: userLabel(v.salesEngineer),
      email: v.salesEngineer?.email ?? '-',
      bu: v.salesEngineer?.bu ?? '-',
      period: v.period,
      amount: v.amount,
      variableStatus: v.variableStatus,
      validatedBySalesEngineer: userLabel(v.validatedBySalesEngineer),
      validatedBySalesEngineerAt: v.validatedBySalesEngineerAt,
      validatedByManager: userLabel(v.validatedByManager),
      validatedByManagerAt: v.validatedByManagerAt,
      sentToLuccaAt: v.sentToLuccaAt,
      luccaVariableId: v.luccaVariableId,
      _canSend: v.variableStatus === 'VALIDATION_MANAGER',
      _canDelete: canDelete,
    }
  }),
)

const selectableCount = computed(() => gridRows.value.filter((r) => r._canSend).length)
const selectedSendable = computed(() => selectedRows.value.filter((r: any) => r._canSend))

function onSelectionChanged(rowsSelected: any[]) {
  selectedRows.value = rowsSelected
}

function askDelete(row: any) {
  rowToDelete.value = row
  showConfirmDelete.value = true
}

async function onConfirmDelete() {
  if (deleting.value || !rowToDelete.value?.luccaVariableId) return
  deleting.value = true
  try {
    const result = await variableStore.deleteFromLuccaById(rowToDelete.value.luccaVariableId)
    if (result.failed.length) {
      $toast.error(`Échec suppression Lucca : ${result.failed[0]?.error ?? 'inconnu'}`)
    } else if (result.reset.length) {
      $toast.success('Variable supprimée de Lucca et remise en VALIDATION_MANAGER')
    } else if (result.notFound.length) {
      $toast.warning('ID Lucca introuvable en base')
    }
    await loadVariables()
  } finally {
    deleting.value = false
    showConfirmDelete.value = false
    rowToDelete.value = null
  }
}

async function onConfirmSend() {
  if (sending.value) return
  const uuids = selectedSendable.value.map((r: any) => r.uuid)
  if (uuids.length === 0) {
    $toast.warning('Aucune variable en VALIDATION_MANAGER sélectionnée')
    showConfirmSend.value = false
    return
  }
  sending.value = true
  try {
    const result = await variableStore.sendBatchToLucca(uuids)
    lastResult.value = result
    if (result.sent.length) $toast.success(`${result.sent.length} variable(s) envoyée(s) à Lucca`)
    if (result.failed.length) $toast.error(`${result.failed.length} échec(s) lors de l'envoi`)
    await loadVariables()
  } finally {
    sending.value = false
    showConfirmSend.value = false
  }
}

const statusBadge = (s: string) => {
  const base = 'px-2 py-0.5 rounded text-xs font-medium text-white '
  switch (s) {
    case 'SENT_TO_LUCCA': return base + 'bg-emerald-500'
    case 'VALIDATION_MANAGER': return base + 'bg-cyan-500'
    case 'VALIDATION_IC': return base + 'bg-orange-500'
    case 'INIT': return base + 'bg-blue-500'
    default: return base + 'bg-gray-400'
  }
}

const cols = computed(() => [
  { field: 'salesEngineer', headerName: 'Commercial', cellClass: 'border-0', filter: 'agTextColumnFilter' },
  { field: 'email', headerName: 'Email', cellClass: 'border-0', filter: 'agTextColumnFilter' },
  { field: 'bu', headerName: 'BU', cellClass: 'border-0', filter: 'agTextColumnFilter' },
  {
    field: 'period',
    headerName: 'Période',
    cellClass: 'border-0',
    filter: false,
    valueFormatter: (p: any) => (p.value ? new Date(p.value).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '-'),
  },
  {
    field: 'amount',
    headerName: 'Montant',
    cellClass: 'border-0',
    filter: false,
    valueFormatter: (p: any) => formatAmount(p.value),
  },
  {
    field: 'variableStatus',
    headerName: 'Statut',
    cellClass: 'border-0',
    filter: 'agTextColumnFilter',
    cellRenderer: (p: any) => {
      const span = document.createElement('span')
      span.className = statusBadge(p.value)
      span.textContent = p.value ?? '-'
      return span
    },
  },
  { field: 'validatedBySalesEngineer', headerName: 'Validé IC', cellClass: 'border-0', filter: false },
  {
    field: 'validatedBySalesEngineerAt',
    headerName: 'Le',
    cellClass: 'border-0',
    filter: false,
    valueFormatter: (p: any) => formatDateTime(p.value),
  },
  { field: 'validatedByManager', headerName: 'Validé Manager', cellClass: 'border-0', filter: false },
  {
    field: 'validatedByManagerAt',
    headerName: 'Le',
    cellClass: 'border-0',
    filter: false,
    valueFormatter: (p: any) => formatDateTime(p.value),
  },
  {
    field: 'sentToLuccaAt',
    headerName: 'Envoyé à Lucca',
    cellClass: 'border-0',
    filter: false,
    valueFormatter: (p: any) => formatDateTime(p.value),
  },
  {
    field: 'luccaVariableId',
    headerName: 'ID Lucca',
    cellClass: 'border-0 font-mono text-xs',
    filter: false,
    valueFormatter: (p: any) => (p.value != null ? String(p.value) : '-'),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellClass: 'border-0 grid-action',
    filter: false,
    sortable: false,
    resizable: false,
    minWidth: 90,
    maxWidth: 90,
    cellRenderer: (params: any) => {
      if (!params.data?._canDelete) {
        const span = document.createElement('span')
        span.className = 'text-xs text-gray-400'
        span.textContent = '-'
        return span
      }
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.title = 'Supprimer de Lucca (< 48h) et repasser en VALIDATION_MANAGER'
      btn.className = 'inline-flex items-center justify-center w-8 h-8 rounded hover:bg-red-50 text-red-600'
      btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/></svg>'
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        askDelete(params.data)
      })
      return btn
    },
  },
])

const failedDetails = computed(() => {
  if (!lastResult.value?.failed?.length) return []
  return lastResult.value.failed.map((f) => {
    const row = rows.value.find((r: any) => r.uuid === f.uuid) as any
    return {
      uuid: f.uuid,
      salesEngineer: row ? userLabel(row.salesEngineer) : f.uuid,
      period: row?.period ? new Date(row.period).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '-',
      error: f.error,
    }
  })
})

onMounted(async () => {
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  if (usersStore.users.length === 0) {
    await usersStore.fetchUsers()
  }
  await loadVariables()
})
</script>

<template>
  <div class="w-full flex flex-col gap-6 p-8 min-h-screen">
    <div class="flex items-center justify-between flex-wrap gap-4">
      <Bs2Title title="Administration variables Lucca" subtitle="Envoyer les variables validées manager à Lucca" />
      <div class="flex items-center gap-2">
        <ButtonDaisy text="Recharger" variant="secondary" :icon="ArrowPathIcon" :disabled="loading" @click="loadVariables" />
        <ButtonDaisy
          :text="sending ? 'Envoi en cours…' : `Envoyer à Lucca (${selectedSendable.length})`"
          variant="primary"
          :icon="PaperAirplaneIcon"
          :disabled="sending || selectedSendable.length === 0"
          @click="showConfirmSend = true"
        />
      </div>
    </div>

    <div class="card bg-base-100 shadow">
      <div class="card-body p-4">
        <div class="flex flex-row flex-wrap items-end gap-2">
          <SelectInput
            v-model="filterStatus"
            label="Statut"
            :options="STATUS_OPTIONS"
            class="flex-1 min-w-[220px] max-w-[320px]"
            @update:model-value="loadVariables"
          />
          <SelectInput
            v-model="filterBu"
            label="BU"
            :options="BU_OPTIONS"
            class="flex-1 min-w-[180px] max-w-[260px]"
            @update:model-value="loadVariables"
          />
          <SelectInput
            v-model="filterUserUuid"
            label="Commercial"
            :options="userOptions"
            placeholder="Tous"
            class="flex-1 min-w-[220px] max-w-[320px]"
            @update:model-value="loadVariables"
          />
          <div class="ml-auto text-sm text-gray-500">
            {{ gridRows.length }} ligne(s) · {{ selectableCount }} envoyable(s)
          </div>
        </div>
      </div>
    </div>

    <div v-if="lastResult && lastResult.failed.length" class="rounded-xl border border-red-300 bg-red-50 p-4">
      <div class="flex items-center gap-2 mb-2">
        <ExclamationTriangleIcon class="w-5 h-5 text-red-600" />
        <h3 class="font-bold text-red-900">Erreurs d'envoi ({{ lastResult.failed.length }})</h3>
        <button class="ml-auto text-xs text-red-700 underline" @click="lastResult = null">Masquer</button>
      </div>
      <ul class="text-sm text-red-800 space-y-1">
        <li v-for="f in failedDetails" :key="f.uuid" class="font-mono">
          <span class="font-semibold">{{ f.salesEngineer }}</span> · {{ f.period }} —
          <span class="break-all">{{ f.error }}</span>
        </li>
      </ul>
    </div>

    <div class="card bg-base-100 shadow">
      <div class="card-body p-4">
        <GridVue
          :list="gridRows"
          :cols="cols"
          :row-selection="{ mode: 'multiRow', selectAll: 'filtered' }"
          :get-row-id="(params: any) => params.data.uuid"
          @onSelectionChanged="onSelectionChanged"
        />
      </div>
    </div>

    <ConfirmModal
      :show-modal="showConfirmDelete"
      title="Supprimer la variable sur Lucca"
      :body="rowToDelete
        ? `La variable de ${rowToDelete.salesEngineer} (${rowToDelete.period ? new Date(rowToDelete.period).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : ''}, ID Lucca ${rowToDelete.luccaVariableId}) va être supprimée de Lucca et repassée en VALIDATION_MANAGER. Possible uniquement dans les 48h suivant l'envoi.`
        : ''"
      confirm-text="Supprimer"
      cancel-text="Annuler"
      confirm-variant="error"
      :loading="deleting"
      @confirm="onConfirmDelete"
      @cancel="showConfirmDelete = false; rowToDelete = null"
    />
    <ConfirmModal
      :show-modal="showConfirmSend"
      title="Envoyer à Lucca"
      :body="`${selectedSendable.length} variable(s) en VALIDATION_MANAGER vont être envoyées à Lucca. Cette action est irréversible côté BS2 tant que les items Lucca n'ont pas été supprimés.`"
      confirm-text="Envoyer"
      cancel-text="Annuler"
      confirm-variant="primary"
      :loading="sending"
      @confirm="onConfirmSend"
      @cancel="showConfirmSend = false"
    />
  </div>
</template>
